import {
  CATEGORY_SECTIONS,
  SPECIFIC_CATEGORY_SECTIONS,
  Exercise,
} from "../category_mappings";

// ONE resolver for personality content, shared by every render surface.
//
// Before 2026-08-20 the resolve chain was copy-pasted into five components with
// three different levels of care — MainResults had the full defensive version
// from ADR 2026-07-20, while PersonalityTab and GeneralPersonalityPage indexed
// the maps directly and would white-screen on an unresolvable key. Removing
// sub-personalities makes that difference load-bearing, so it is centralised
// here rather than fixed five times.
//
// ⛔ NEVER index CATEGORY_SECTIONS[...] directly at a call site (ADR
// 2026-07-20, ClickUp 86bb0gg67). Quiz results persist in localStorage
// indefinitely while the content maps change underneath them; a bare index on a
// retired personality name threw inside MainResults and, with no error boundary
// at the time, unmounted the entire app to a blank page.

export const EMPTY_SECTION = {
  Subtext: "",
  Overview: "",
  DrivenBy: "",
  MeasuresSuccessBy: "",
  NaturalStrength: "",
  WatchOut: "",
  FamousExample: ["", ""],
  DominantTraits: [] as string[],
  Strengths: [] as string[],
  Weaknesses: [] as string[],
  GrowthOpportunities: [] as string[],
  BehavioralConcepts: [] as string[],
  Exercises: [] as Exercise[],
};

export type ResolvedSection = typeof EMPTY_SECTION;

/**
 * Content for one of the five core personalities.
 *
 * Falls back to a neutral empty section — never throws, never invents copy —
 * and warns with the offending key so an unresolvable stored result leaves a
 * trace in the console rather than failing silently.
 */
export function resolveCoreSection(
  category: string | undefined | null,
  surface = "unknown"
): ResolvedSection {
  // Two jobs, and both are load-bearing:
  //  - TYPES: `category` is nullable and CATEGORY_SECTIONS is a Record<string,…>,
  //    so indexing without this is a compile error (TS2538). A test suite cannot
  //    see that — babel-jest strips types — so a mutation run will report this
  //    line as SURVIVED. The build is the checker for it.
  //  - RUNTIME: "no personality yet" is an ORDINARY state (nothing chosen, quiz
  //    not taken), not an anomaly. Falling through to the branch below would
  //    warn on it every render and bury the warnings that mean something.
  if (!category) return EMPTY_SECTION;
  const section = CATEGORY_SECTIONS[category];
  if (!section) {
    console.warn(
      `[${surface}] personality "${category}" has no content section; rendering empty content.`
    );
    return EMPTY_SECTION;
  }
  return section;
}

/**
 * Workbook exercises for a result.
 *
 * As of 2026-09-21 exercises exist on BOTH tiers:
 *   - CATEGORY_SECTIONS (the five cores) — McKayla's re-authored exercises
 *   - SPECIFIC_CATEGORY_SECTIONS (the ten sub-personalities) — legacy exercises
 *
 * Pre-2026-08-20 users have a stored `specificDominantCategory` with saved
 * answers on the server keyed to the sub-type exercises. Their workbook
 * continues to use the sub-type exercises so their saved answers stay aligned.
 *
 * Post-2026-08-20 users (no sub-personality) get the core personality exercises.
 */
export function resolveWorkbookExercises(
  storedSpecificCategory: string | undefined | null,
  coreCategory?: string | undefined | null
): Exercise[] {
  if (storedSpecificCategory) {
    return SPECIFIC_CATEGORY_SECTIONS[storedSpecificCategory]?.Exercises ?? [];
  }
  if (coreCategory) {
    return CATEGORY_SECTIONS[coreCategory]?.Exercises ?? [];
  }
  return [];
}
